/**
 * Weekday helpers shared by class + blackout recurrence UIs.
 *
 * Wire format: recurrence/blackout weekday arrays from the API follow **Python
 * `datetime.weekday()`** — Monday = 0 … Sunday = 6.
 *
 * JavaScript `Date.prototype.getDay()` uses **Sunday = 0 … Saturday = 6**.
 *
 * If the UI sends JS-style numbers directly, Thursday (JS 4) is stored as 4,
 * which Python treats as **Friday** — the classic off-by-one on Thu/Fri. Always
 * convert at the boundary with the helpers below.
 */

export function jsGetDayToPythonWeekday(js) {
  const n = ((Number(js) % 7) + 7) % 7;
  return n === 0 ? 6 : n - 1;
}

export function pythonWeekdayToJsGetDay(py) {
  const n = ((Number(py) % 7) + 7) % 7;
  return n === 6 ? 0 : n + 1;
}

/** Weekday checkbox options. `dow` is JS `getDay()` (Sun=0). Mon→Sun order. */
export const WEEKDAYS = [
  { key: "mon", label: "Mon", dow: 1 },
  { key: "tue", label: "Tue", dow: 2 },
  { key: "wed", label: "Wed", dow: 3 },
  { key: "thu", label: "Thu", dow: 4 },
  { key: "fri", label: "Fri", dow: 5 },
  { key: "sat", label: "Sat", dow: 6 },
  { key: "sun", label: "Sun", dow: 0 },
];

/** Checkbox state with every day toggled on/off. */
function weekdayStateWith(value) {
  return WEEKDAYS.reduce((acc, { key }) => {
    acc[key] = value;
    return acc;
  }, {});
}

/** `{ mon: false, … }` — no days selected (blackout default). */
export const emptyWeekdays = () => weekdayStateWith(false);

/** `{ mon: true, … }` — all days selected (new daily class default). */
export const allWeekdaysSelected = () => weekdayStateWith(true);

/** Checkbox state → sorted Python-weekday ints (0=Mon…6=Sun). For submit payloads. */
export function weekdayStateToPython(state) {
  return WEEKDAYS.filter(({ key }) => state[key])
    .map(({ dow }) => jsGetDayToPythonWeekday(dow))
    .sort((a, b) => a - b);
}

/** Python-weekday ints → checkbox state (unlisted days default to false). For edit-load. */
export function weekdayStateFromPython(pythonWeekdays) {
  const selectedDow = dailyWeekdaySet(pythonWeekdays);
  return WEEKDAYS.reduce((acc, { key, dow }) => {
    acc[key] = Boolean(selectedDow?.has(dow));
    return acc;
  }, {});
}

/**
 * Python-weekday ints → Set of JS `getDay()` ints, or `null` when none/empty.
 * `null` means "every day" (backward compat). For preview-expander weekday filtering.
 */
export function dailyWeekdaySet(pythonWeekdays) {
  if (!Array.isArray(pythonWeekdays) || pythonWeekdays.length === 0)
    return null;
  return new Set(
    pythonWeekdays.map((py) => pythonWeekdayToJsGetDay(Number(py)))
  );
}

/**
 * Label suffix for a daily class's selected weekdays, e.g. `(Mon, Wed, Fri)`.
 * Returns "" when none/empty or all 7 selected (caller shows a plain "Daily").
 */
export function formatDailyWeekdays(pythonWeekdays) {
  const selectedDow = dailyWeekdaySet(pythonWeekdays);
  if (!selectedDow || selectedDow.size >= WEEKDAYS.length) return "";
  const labels = WEEKDAYS.filter(({ dow }) => selectedDow.has(dow)).map(
    ({ label }) => label
  );
  return labels.length ? `(${labels.join(", ")})` : "";
}
